/**
 * External dependencies
 */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { noop } from "lodash";
import { Box, Text } from "@towersystems/towersystems-design-system";
import DropdownLink from "components/dropdown-link";
import moment from "moment";
import Dropdown from "react-dropdown";

/**
 * Internal dependencies
 */
import {
  getDateRange,
  getCompareDateRange,
  getEndDate,
  getStartDate,
  formatDateString,
  formatCompareString,
} from "state/ui/date-range/selectors";
import { setDateRange } from "state/ui/date-range/actions";
import dateParser from "lib/date-parser";
import Button from "components/button";
import ButtonGroup from "components/button-group";

/**
 * Style dependencies
 */
import "./styles.scss";

export const options = [
  { label: "Today", value: "Today" },
  { label: "Yesterday", value: "Yesterday" },
  { label: "This week", value: "This week" },
  { label: "Last week", value: "Last week" },
  { label: "Last 30 days", value: "Last 30 days" },
  { label: "Last 60 days", value: "Last 60 days" },
  { label: "Last 180 days", value: "Last 180 days" },
  { label: "This year", value: "This year" },
];

const DateRangePicker = ({
  dateRange = noop,
  defaultStartDate,
  defaultEndDate,
  compareDateRange,
  formatedDateString,
  formatedCompareString,
  ...props
}) => {
  const [compareToType, setCompareToType] = useState("previous");
  const [dropdownDateRange, setDropdownDateRange] = useState(dateRange);

  const [dates, setDates] = useState({
    startDate: defaultStartDate && moment(defaultStartDate),
    endDate: moment(defaultEndDate),
  });

  const [focused, setFocused] = useState("startDate");

  useEffect(() => {
    try {
      let dates = dateParser(dateRange);
      setDates({ startDate: moment(dates[0]), endDate: moment(dates[1]) });
    } catch (error) {}
  }, []);

  // Force the focusedInput to always be truthy so that dates are always selectable
  const onFocusChange = (focusedInput) => {
    setFocused(!focusedInput ? "startDate" : focusedInput);
  };

  const onDatesChange = ({ startDate, endDate }) => {
    setDates({ startDate, endDate });
  };

  const onDropdownRangeChange = ({ value }) => {
    try {
      let dates = dateParser(value);
      setDates({ startDate: moment(dates[0]), endDate: moment(dates[1]) });
    } catch (error) {}
    setDropdownDateRange(value);
  };

  const applyFilters = () => {
    let startDateString =
      dates.startDate && dates.startDate.format("YYYY-MM-DD");
    let endDateString = dates.endDate && dates.endDate.format("YYYY-MM-DD");

    props.setDateRange(
      dropdownDateRange,
      startDateString,
      endDateString || startDateString,
      compareToType
    );
  };

  // format strings
  const startDateString =
    dates.startDate && dates.startDate.format("YYYY-MM-DD");

  const endDateString = dates.endDate && dates.endDate.format("YYYY-MM-DD");

  return (
    <Box className="date-range-picker" display="flex" alignItems="center">
      <Box className="date-range-picker__dropdown-wrapper">
        <Box>
          <DropdownLink
            className="date-range-picker__dropdown"
            label={formatedDateString}
            icon={{ name: "BsCalendar" }}
            showArrow={false}
          >
            {({ close }) => {
              return (
                <Box>
                  <Box padding="large">
                    <Text>Date range</Text>
                    <Dropdown
                      value={dropdownDateRange}
                      options={options}
                      onChange={onDropdownRangeChange}
                      placeholder="Custom"
                    />
                  </Box>
                  <Box
                    marginBottom="large"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    padding="large"
                  >
                    <Box width="full" marginRight="medium">
                      <Text>Starting</Text>
                      <input
                        type="date"
                        name="start date"
                        value={startDateString}
                        onChange={(e) => {
                          setDropdownDateRange(null);
                          setDates({
                            ...dates,
                            startDate: moment(e.target.value),
                          });
                        }}
                      />
                    </Box>
                    <Box width="full" marginLeft="medium">
                      <Text>Ending</Text>
                      <input
                        type="date"
                        name="end date"
                        value={endDateString}
                        onChange={(e) => {
                          setDropdownDateRange(null);
                          setDates({
                            ...dates,
                            endDate: moment(e.target.value),
                          });
                        }}
                      />
                    </Box>
                  </Box>
                  <Box padding="large"></Box>
                  <Box padding="large">
                    <Text>Compare to</Text>

                    <Dropdown
                      value={compareToType}
                      options={[
                        { label: "Previous Period", value: "previous" },
                        { label: "Last Year", value: "last_year" },
                      ]}
                      onChange={({ value }) => {
                        setCompareToType(value);
                      }}
                    />
                  </Box>
                  <ButtonGroup
                    padding="large"
                    display="flex"
                    alignItems="center"
                    justifyContent="spaceBetween"
                  >
                    <Button
                      width="full"
                      onClick={() => {
                        close();
                        applyFilters();
                      }}
                      primary
                    >
                      Apply
                    </Button>
                    <Button width="full" onClick={() => close()}>
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Box>
              );
            }}
          </DropdownLink>
        </Box>
        <Box marginTop="small">
          <Text size="xsmall">Compared to {formatedCompareString}</Text>
        </Box>
      </Box>
    </Box>
  );
};

export default connect(
  (state) => ({
    dateRange: getDateRange(state),
    compareDateRange: getCompareDateRange(state),
    defaultEndDate: getEndDate(state),
    defaultStartDate: getStartDate(state),
    formatedDateString: formatDateString(state),
    formatedCompareString: formatCompareString(state),
  }),
  {
    setDateRange,
  }
)(DateRangePicker);
